// src/app/settings/accruals/pages/leave-transactions/leave-transactions.component.ts

import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subject, forkJoin } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  finalize,
  takeUntil,
} from 'rxjs/operators';

import { AccrualsService } from '../accruals.service';
import {
  EmployeeUser,
  LeaveAccrualLedger,
  LeavePolicyFilterValues,
  LeaveType,
} from '../models/accruals.model';
import { AccrualsNavComponent } from '../../../shared/accruals-nav/accruals-nav.component';

interface TransactionFiltersVm {
  searchTerm: string;
  employeeId: number | null;
  leaveType: LeaveType | null;
  entryType: string | null;
  startDate: string | null;
  endDate: string | null;
}

@Component({
  selector: 'app-leave-transactions',
  standalone: true,
  imports: [CommonModule, FormsModule, AccrualsNavComponent],
  templateUrl: './leave-transactions.component.html',
  styleUrls: ['./leave-transactions.component.scss'],
})
export class LeaveTransactionsComponent implements OnInit, OnDestroy {
  isLoading = false;
  errorMessage: string | null = null;

  allTransactions: LeaveAccrualLedger[] = [];
  filteredTransactions: LeaveAccrualLedger[] = [];

  allUsers: EmployeeUser[] = [];
  leavePolicyFilterValues: LeavePolicyFilterValues | null = null;
  entryTypes: string[] = [];

  currentPage = 1;
  totalPages = 1;
  pageSize = 25;
  pageSizes = [25, 50, 100, 250];

  filters: TransactionFiltersVm = {
    searchTerm: '',
    employeeId: null,
    leaveType: null,
    entryType: null,
    startDate: null,
    endDate: null,
  };

  searchInputValue = '';
  private readonly searchInput$ = new Subject<string>();
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly accrualsService: AccrualsService,
    private readonly cd: ChangeDetectorRef,
  ) { }

  ngOnInit(): void {
    this.bindSearch();
    this.loadInitialData();
  }

  ngOnDestroy(): void {
    this.searchInput$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  get hasActiveFilters(): boolean {
    return !!(
      this.filters.searchTerm.trim() ||
      this.filters.employeeId !== null ||
      this.filters.leaveType !== null ||
      this.filters.entryType !== null ||
      this.filters.startDate ||
      this.filters.endDate
    );
  }

  get pagedTransactions(): LeaveAccrualLedger[] {
    const startIndex = (this.currentPage - 1) * this.pageSize;
    return this.filteredTransactions.slice(startIndex, startIndex + this.pageSize);
  }

  loadInitialData(): void {
    this.isLoading = true;
    this.errorMessage = null;
    this.cd.detectChanges();

    forkJoin({
      transactions: this.accrualsService.getAllLeaveAccrualLedgers(),
      users: this.accrualsService.getAllUsers(),
      leavePolicyFilterValues: this.accrualsService.getLeavePolicyFilterValues(),
    })
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.cd.detectChanges();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: ({ transactions, users, leavePolicyFilterValues }) => {
          this.allTransactions = [...(transactions ?? [])].sort((a, b) =>
            String(b.effectiveDate).localeCompare(String(a.effectiveDate)),
          );

          this.filteredTransactions = [...this.allTransactions];
          this.currentPage = 1;
          this.updatePagination();

          this.allUsers = [...(users ?? [])].sort((a, b) =>
            `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`),
          );

          this.leavePolicyFilterValues = leavePolicyFilterValues;

          this.entryTypes = [
            ...new Set(this.allTransactions.map((x) => x.entryType).filter(Boolean)),
          ].sort((a, b) => a.localeCompare(b));

          this.cd.detectChanges();
        },
        error: () => {
          this.errorMessage = 'Failed to load leave transactions.';
          this.allTransactions = [];
          this.filteredTransactions = [];
          this.currentPage = 1;
          this.totalPages = 1;
          this.cd.detectChanges();
        },
      });
  }

  private bindSearch(): void {
    this.searchInput$
      .pipe(debounceTime(1000), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((value) => {
        this.filters.searchTerm = value;
        this.currentPage = 1;
        this.applyFilters();
      });
  }

  onSearchTermChange(value: string | null): void {
    const normalized = String(value ?? '').trimStart();
    this.searchInputValue = normalized;
    this.searchInput$.next(normalized);
  }

  onEmployeeChange(value: number | string | null): void {
    this.filters.employeeId = this.toNullableNumber(value);
    this.currentPage = 1;
    this.applyFilters();
  }

  onLeaveTypeChange(value: LeaveType | '' | null): void {
    this.filters.leaveType = value ? value : null;
    this.currentPage = 1;
    this.applyFilters();
  }

  onEntryTypeChange(value: string | null): void {
    this.filters.entryType = value ? value : null;
    this.currentPage = 1;
    this.applyFilters();
  }

  onStartDateChange(value: string | null): void {
    this.filters.startDate = value || null;
    this.currentPage = 1;
    this.applyFilters();
  }

  onEndDateChange(value: string | null): void {
    this.filters.endDate = value || null;
    this.currentPage = 1;
    this.applyFilters();
  }

  clearFilters(): void {
    this.filters = {
      searchTerm: '',
      employeeId: null,
      leaveType: null,
      entryType: null,
      startDate: null,
      endDate: null,
    };

    this.searchInputValue = '';
    this.currentPage = 1;
    this.applyFilters();
  }

  applyFilters(): void {
    const search = this.filters.searchTerm.trim().toLowerCase();
    const startDate = this.filters.startDate ? new Date(this.filters.startDate) : null;
    const endDate = this.filters.endDate ? new Date(this.filters.endDate) : null;

    if (endDate) {
      endDate.setHours(23, 59, 59, 999);
    }

    this.filteredTransactions = this.allTransactions.filter((row) => {
      const employeeFullName = `${row.employee.firstName} ${row.employee.lastName}`.toLowerCase();
      const rowDate = new Date(row.effectiveDate);

      const matchesSearch =
        !search ||
        employeeFullName.includes(search) ||
        row.employee.employeeNumber.toLowerCase().includes(search) ||
        row.leaveType.toLowerCase().includes(search) ||
        row.entryType.toLowerCase().includes(search) ||
        String(row.referenceId ?? '').toLowerCase().includes(search);

      const matchesEmployee =
        this.filters.employeeId === null || row.employee.id === this.filters.employeeId;

      const matchesLeaveType =
        this.filters.leaveType === null || row.leaveType === this.filters.leaveType;

      const matchesEntryType =
        this.filters.entryType === null || row.entryType === this.filters.entryType;

      const matchesStartDate = !startDate || rowDate >= startDate;
      const matchesEndDate = !endDate || rowDate <= endDate;

      return (
        matchesSearch &&
        matchesEmployee &&
        matchesLeaveType &&
        matchesEntryType &&
        matchesStartDate &&
        matchesEndDate
      );
    });

    this.updatePagination();
    this.cd.detectChanges();
  }

  changePage(page: number): void {
    if (page < 1 || page > this.totalPages) {
      return;
    }

    this.currentPage = page;
    this.cd.detectChanges();
  }

  changePageSize(size: number): void {
    this.pageSize = Number(size);
    this.currentPage = 1;
    this.updatePagination();
    this.cd.detectChanges();
  }

  getLeaveTypeClass(leaveType: LeaveType): string {
    return leaveType === 'Sick' ? 'sick' : 'vacation';
  }

  getEntryTypeClass(entryType: string): string {
    const value = entryType.toLowerCase();

    if (value.includes('manual')) return 'manual';
    if (value.includes('accrual')) return 'accrual';
    if (value.includes('approved')) return 'approved';
    if (value.includes('cancel')) return 'cancelled';
    if (value.includes('reject')) return 'rejected';

    return 'default';
  }

  getHoursClass(hours: number): string {
    return hours < 0 ? 'negative' : 'positive';
  }

  trackByTransactionId(_: number, item: LeaveAccrualLedger): number {
    return item.id;
  }

  private updatePagination(): void {
    this.totalPages = Math.max(1, Math.ceil(this.filteredTransactions.length / this.pageSize));

    if (this.currentPage > this.totalPages) {
      this.currentPage = this.totalPages;
    }
  }

  private toNullableNumber(value: number | string | null | undefined): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }
}